import { buildNextSteps } from "../../components/panel/nextSteps";
import { weekdayShortLabel } from "../../lib/dateWindow";
import { addDaysIso, cycleWindow } from "../../lib/rosterDatesWindow";
/**
 * Pure facts for the home page team overview (#2640).
 *
 * Everything the tiles show is derived here from the reads the page makes:
 * the team list, `GET /api/account/me/team-overview`, the member's own
 * shifts, leave and inbox. No React and no I/O, so the day states, the pose
 * rule, the chip words and the links are unit-tested on their own
 * (`web/tests/home/homeOverview.test.ts`). Date maths comes from
 * `lib/rosterDatesWindow`; the day state rule is `solveRowState`.
 */
import { seatIconOf } from "../../memberapp/calendar/calendarChips";
import { type CalendarAssignment, windowOf } from "../../memberapp/calendar/calendarModel";
import { rosterHrefForSolve, solveRowState } from "../team/SolvesList";
import type { OverviewCycle as HomeCycle, TeamOverviewRow } from "./overviewTypes";

// ── Greeting ─────────────────────────────────────────────────────────

/** Morning until noon, afternoon until 17:00, evening after. */
export function greetingFor(hour: number): string {
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  return "Good evening";
}

/** The name after the greeting: none while auth loads, and none in a demo
 * (#2824), where the account is a throwaway with no real name. */
export function greetingName(opts: {
  loading: boolean;
  inDemo: boolean;
  displayName: string | null | undefined;
  email: string | null | undefined;
}): string | null {
  if (opts.loading || opts.inDemo) return null;
  return opts.displayName || opts.email || null;
}

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** "Monday 19 October". */
export function dateLine(d: Date): string {
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** The month name for the desk-calendar page ("October"). */
export function monthName(d: Date): string {
  return MONTHS[d.getMonth()] ?? "";
}

/** The weekday name for the desk-calendar page ("Monday"). */
export function weekdayName(d: Date): string {
  return WEEKDAYS[d.getDay()] ?? "";
}

/** The speech bubble: the total across teams. */
export function thingsLine(total: number): string {
  if (total <= 0) return "All clear today.";
  return total === 1 ? "1 thing needs you." : `${total} things need you.`;
}

// ── Tile identity ────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  lead: "Lead",
  co_lead: "Co-lead",
  member: "Member",
  observer: "Observer",
};

/** The role chip text; never the raw key. */
export function roleLabel(role: string | null | undefined): string {
  if (!role) return "Member";
  const known = ROLE_LABELS[role];
  if (known) return known;
  const spaced = role.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The grade chip: the title when set, else the grade label, else none. */
export function gradeChipOf(row: TeamOverviewRow | undefined): string | null {
  if (!row) return null;
  return row.title || row.grade_label || null;
}

export const CAST = ["maya", "jo", "alex", "sam"] as const;
export type CastMember = (typeof CAST)[number];

/** A stable cast member per team (a hash of the id). */
export function castFor(teamId: string): CastMember {
  let sum = 0;
  for (let i = 0; i < teamId.length; i += 1) sum += teamId.charCodeAt(i);
  return CAST[sum % CAST.length] ?? "maya";
}

export type Pose = "idle" | "point" | "celebrate";

// ── Calendar ─────────────────────────────────────────────────────────

export type CellTone =
  | "published"
  | "draft"
  | "solving"
  | "none"
  | "day"
  | "night"
  | "shift"
  | "leave"
  | "off";

export interface CalendarCell {
  iso: string;
  num: number;
  today: boolean;
  tone: CellTone;
  /** The full meaning, for `aria-label` and `title`. */
  label: string;
  /** Where a tap goes: that team's roster at this week. */
  href?: string;
}

export interface CalendarWeek {
  label: "This" | "Next";
  from: string;
  cells: CalendarCell[];
}

const TONE_WORDS: Record<CellTone, string> = {
  published: "published",
  draft: "draft, not published",
  solving: "solving",
  none: "not solved",
  day: "day shift",
  night: "night shift",
  shift: "shift",
  leave: "leave",
  off: "nothing planned",
};

function dayNumber(iso: string): number {
  return Number(iso.slice(8, 10));
}

function weekSkeleton(
  monday: string,
  today: string,
  toneOf: (iso: string) => CellTone,
  teamId: string,
): CalendarWeek[] {
  return (["This", "Next"] as const).map((label, w) => {
    const from = addDaysIso(monday, w * 7);
    const week: CalendarWeek = {
      label,
      from,
      cells: [],
    };
    const href = rosterHrefForSolve(teamId, { horizon_start: from, horizon_days: 7 });
    for (let i = 0; i < 7; i += 1) {
      const iso = addDaysIso(from, i);
      const tone = toneOf(iso);
      week.cells.push({
        iso,
        num: dayNumber(iso),
        today: iso === today,
        tone,
        label: `${weekdayShortLabel(iso)}, ${TONE_WORDS[tone]}`,
        href,
      });
    }
    return week;
  });
}

function liveWindowsOn(iso: string, cycles: readonly HomeCycle[]): HomeCycle[] {
  return cycles.filter((c) => {
    if (c.phase === "archived") return false;
    const w = cycleWindow(c);
    return iso >= w.from && iso <= w.to;
  });
}

function leadToneOf(iso: string, cycles: readonly HomeCycle[]): CellTone {
  const covering = liveWindowsOn(iso, cycles);
  if (covering.length === 0) return "none";
  // Several windows on one day: the newest one is the live one.
  const newest = [...covering].sort((a, b) =>
    (b.created_at ?? "").localeCompare(a.created_at ?? ""),
  )[0];
  if (!newest) return "none";
  const state = solveRowState(newest as unknown as Parameters<typeof solveRowState>[0]);
  if (state === "published") return "published";
  if (state === "solved") return "draft";
  if (state === "solving") return "solving";
  return "none";
}

/**
 * The lead's two weeks: each day shows the live window that covers it, with
 * the Solves list's own state rule. Days no window covers read "none".
 * `teamId` is only used for the day links.
 */
export function leadWeeks(
  monday: string,
  today: string,
  cycles: readonly HomeCycle[],
  teamId = "",
): CalendarWeek[] {
  return weekSkeleton(monday, today, (iso) => leadToneOf(iso, cycles), teamId);
}

export interface MemberShift
  extends Partial<Omit<CalendarAssignment, "date" | "team_id" | "role">> {
  date: string;
  team_id: string;
  role?: string;
}

export interface MemberLeave {
  team_id?: string;
  start_date: string;
  end_date: string;
  request_type: string;
  status?: string;
}

/** Leave that holds the roster: planned or confirmed (absent = old API). */
function leaveCounts(l: MemberLeave): boolean {
  if (l.request_type !== "leave") return false;
  return l.status === undefined || l.status === "planned" || l.status === "confirmed";
}

function memberToneOf(
  iso: string,
  teamId: string,
  shifts: readonly MemberShift[],
  leaves: readonly MemberLeave[],
): CellTone {
  const onLeave = leaves.some(
    (l) =>
      leaveCounts(l) &&
      (l.team_id === undefined || l.team_id === teamId) &&
      iso >= l.start_date &&
      iso <= l.end_date,
  );
  if (onLeave) return "leave";
  const mine = shifts.filter((s) => s.team_id === teamId && s.date === iso);
  const first = mine[0];
  if (!first) return "off";
  // The same sun-or-moon rule the phone calendar uses; no derived hours, no guess.
  const icon = seatIconOf(windowOf(first as CalendarAssignment));
  if (icon === "sun") return "day";
  if (icon === "moon") return "night";
  return first.is_oncall ? "night" : "shift";
}

/** The member's two weeks: their own shifts and approved leave on this team. */
export function memberWeeks(
  monday: string,
  today: string,
  teamId: string,
  source: { assignments: readonly MemberShift[]; leaves: readonly MemberLeave[] },
): CalendarWeek[] {
  return weekSkeleton(
    monday,
    today,
    (iso) => memberToneOf(iso, teamId, source.assignments, source.leaves),
    teamId,
  );
}

/** Whether every day of both weeks is published. */
function allPublished(weeks: readonly CalendarWeek[]): boolean {
  const cells = weeks.flatMap((w) => w.cells);
  return cells.length > 0 && cells.every((c) => c.tone === "published");
}

/**
 * The tile character's pose: points when something waits for the person,
 * celebrates when both weeks are published and nothing waits, else idle.
 */
export function poseFor(needsCount: number, weeks: readonly CalendarWeek[]): Pose {
  if (needsCount > 0) return "point";
  return allPublished(weeks) ? "celebrate" : "idle";
}

// ── Needs-you chips ──────────────────────────────────────────────────

export type NeedKind = "leave" | "swap" | "message" | "task";

export interface NeedChip {
  kind: NeedKind;
  count: number;
  /** The one short word beside the count. */
  word: string;
  /** The full meaning, for `aria-label` and `title`. */
  label: string;
}

function chip(
  kind: NeedKind,
  count: number,
  one: string,
  many: string,
  label: (n: number) => string,
): NeedChip {
  return { kind, count, word: count === 1 ? one : many, label: label(count) };
}

/**
 * The chips of one tile. A lead or co-lead reads the overview's counts; a
 * member reads `member_needs` (swap asks and tasks) from the same row.
 * Zero counts make no chip.
 */
export function needChips(
  role: string | null | undefined,
  row: TeamOverviewRow | undefined,
): NeedChip[] {
  const chips: NeedChip[] = [];
  if (role === "lead" || role === "co_lead") {
    const n = row?.needs_you;
    const leave = n?.leave_to_review ?? 0;
    const swaps = n?.swaps_to_confirm ?? 0;
    const messages = n?.messages_unread ?? 0;
    if (leave > 0) {
      chips.push(
        chip(
          "leave",
          leave,
          "leave",
          "leave",
          (c) => `${c} leave ${c === 1 ? "request" : "requests"} to decide`,
        ),
      );
    }
    if (swaps > 0) {
      chips.push(
        chip(
          "swap",
          swaps,
          "swap",
          "swaps",
          (c) => `${c} ${c === 1 ? "swap" : "swaps"} to confirm`,
        ),
      );
    }
    if (messages > 0) {
      chips.push(
        chip(
          "message",
          messages,
          "message",
          "messages",
          (c) => `${c} unread ${c === 1 ? "message" : "messages"}`,
        ),
      );
    }
    return chips;
  }
  if (role === "member" && row) {
    const swaps = row.member_needs?.swap_asks ?? 0;
    const tasks = row.member_needs?.tasks ?? 0;
    if (swaps > 0) {
      chips.push(
        chip("swap", swaps, "swap", "swaps", (c) => `${c} ${c === 1 ? "swap" : "swaps"} to answer`),
      );
    }
    if (tasks > 0) {
      chips.push(
        chip("task", tasks, "task", "tasks", (c) => `${c} ${c === 1 ? "task" : "tasks"} to do`),
      );
    }
  }
  return chips;
}

/** The total across every team's chips. */
export function totalNeeds(perTeam: readonly (readonly NeedChip[])[]): number {
  return perTeam.reduce((sum, chips) => sum + chips.reduce((s, c) => s + c.count, 0), 0);
}

export interface ChipContext {
  /** The live window holding today (else the first), for the roster rows. */
  cycleId: string | null;
  /** The window the leave count is against (from the API). */
  leaveCycleId: string | null;
  /** The window holding the accepted swaps (from the API); else `cycleId`. */
  swapCycleId?: string | null;
  member: boolean;
}

/**
 * Where a chip opens: the href `buildNextSteps` gives that kind of step on
 * that team (the lead's panel, the same words and links). A member's chips
 * open the member inbox. With no window to open, the team home.
 */
export function chipHref(teamId: string, kind: NeedKind, count: number, ctx: ChipContext): string {
  if (ctx.member) return "/me/inbox";
  const home = `/teams/${teamId}`;
  const cycleId = kind === "swap" ? (ctx.swapCycleId ?? ctx.cycleId) : ctx.cycleId;
  const scope = cycleId ? "cycle" : "team";
  const rows = buildNextSteps({
    scope,
    teamId,
    cycleId: cycleId ?? undefined,
    leave:
      kind === "leave" && ctx.leaveCycleId
        ? { toReview: count, urgent: 0, cycleId: ctx.leaveCycleId }
        : undefined,
    swapsHeld: kind === "swap" ? count : undefined,
    unreadMessages: kind === "message" ? count : undefined,
  });
  const wanted = kind === "message" ? "messages" : kind === "swap" ? "swaps" : kind;
  return rows.find((r) => r.kind === wanted)?.to ?? home;
}

/** The window the lead's roster-scoped links use: holds today, else the first. */
export function currentCycleId(
  today: string,
  cycles: readonly HomeCycle[] | undefined,
): string | null {
  const live = (cycles ?? [])
    .filter((c) => c.phase !== "archived")
    .sort((a, b) => a.horizon_start.localeCompare(b.horizon_start));
  const holding = live.find((c) => {
    const w = cycleWindow(c);
    return today >= w.from && today <= w.to;
  });
  return (holding ?? live[0])?.id ?? null;
}
